import { useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../components/context/AppContextProvider';
import { IRootState } from '../redux/rootReducer';

/**
 * Custom hook to manage RetentionZen widget script injection
 *
 * Requirements:
 * - Only loads when user is logged in
 * - Only loads when property is selected
 * - Prevents duplicate script injections
 * - Refreshes when property changes
 *
 * @returns void
 */
const useRetentionZen = () => {
    const { adminAccessToken, userInfo } = useContext(AppContext);
    const location = useLocation();
    const scriptLoadedRef = useRef<string | null>(null); // Track which property has the script loaded
    const scriptElementRef = useRef<HTMLScriptElement | null>(null);
    const initScriptRef = useRef<HTMLScriptElement | null>(null);

    // Get selected location from Redux state
    const {
        data: locationDetail,
        loading: locationDetailLoading,
        errorMsg: locationDetailError,
    } = useSelector((state: IRootState) => state.locationReducer.locationDetail);

    // Extract property name from Redux state or location data
    const getPropertyName = (): string | null => {
        // Check if locationDetail has data (from Redux - when property is selected)
        if (locationDetail?.locationName) {
            return locationDetail.locationName;
        }
        // Fallback to name property if locationName doesn't exist
        if (locationDetail?.name) {
            return locationDetail.name;
        }

        // Check location state from navigation
        const locationState = location.state as { locationName?: string; location?: { name?: string } } | null;
        if (locationState?.locationName) {
            return locationState.locationName;
        }
        if (locationState?.location?.name) {
            return locationState.location.name;
        }

        return null;
    };

    // Get property ID for tracking purposes (to detect property changes)
    const getPropertyId = (): string | null => {
        // Check URL params first (e.g., /location/:id, /visits with locationId in state)
        const pathMatch = location.pathname.match(/\/location\/([^/]+)/);
        if (pathMatch && pathMatch[1]) {
            return pathMatch[1];
        }

        // Check location state from navigation
        const locationState = location.state as { locationId?: string } | null;
        if (locationState?.locationId) {
            return locationState.locationId;
        }

        // Check if locationById has data (from Redux - when property is selected)
        if (locationDetail?.id) {
            return locationDetail.id;
        }

        return null;
    };

    // Check if user is logged in
    const isLoggedIn = (): boolean => {
        return !!(adminAccessToken || localStorage.getItem('adminAccessToken'));
    };

    // Get user email
    const getUserEmail = (): string | null => {
        if (userInfo?.email) {
            return userInfo.email;
        }
        return localStorage.getItem('email');
    };

    // Clean up existing scripts
    const cleanupScripts = (propertyName?: string | null) => {
        let cleanedUp = false;
        const propertyInfo = propertyName ? `for property Name: ${propertyName}` : '';

        // Remove init script
        if (initScriptRef.current && initScriptRef.current.parentNode) {
            initScriptRef.current.parentNode.removeChild(initScriptRef.current);
            initScriptRef.current = null;
            cleanedUp = true;
            console.log(`RetentionZen: Cleaned up init script ${propertyInfo}`);
        }

        // Remove widget script
        if (scriptElementRef.current && scriptElementRef.current.parentNode) {
            scriptElementRef.current.parentNode.removeChild(scriptElementRef.current);
            scriptElementRef.current = null;
            cleanedUp = true;
            console.log(`RetentionZen: Cleaned up widget script ${propertyInfo}`);
        }

        // Reset RetentionZenWidget if it exists
        if (window.RetentionZenWidget) {
            try {
                // Try to destroy/reset the widget if the API supports it
                if (typeof window.RetentionZenWidget.destroy === 'function') {
                    window.RetentionZenWidget.destroy();
                    cleanedUp = true;
                    console.log(`RetentionZen: Widget destroyed ${propertyInfo}`);
                }
            } catch (error) {
                console.warn('Error cleaning up RetentionZen widget:', error);
            }
        }

        // Log success if cleanup occurred
        if (cleanedUp) {
            const userEmail = getUserEmail();
            console.log(`RetentionZen widget cleaned up successfully ${propertyInfo} with email: ${userEmail}`);
        }
    };

    // Initialize RetentionZen widget
    const initializeWidget = (propertyName: string, propertyId: string, userEmail: string) => {
        if (window.RetentionZenWidget) {
            try {
                window.RetentionZenWidget.init({
                    productSlug: 'pineapple',
                    apiKey: 'wdg_640b2a55f99472934f813c34514bdd02fc739e30cf6e91dd311195c5eef8ca0c',
                    accountId: propertyName, // Use property/location name as accountId
                    userId: userEmail, // Use user's email as userId
                    email: userEmail, // User's email for identity stitching
                });

                // Log to console as requested
                console.log('RetentionZen widget initialized for property:', propertyName, 'with email:', userEmail);

                // Mark as loaded for this property (using ID for tracking)
                scriptLoadedRef.current = propertyId;
            } catch (error) {
                console.error('Error initializing RetentionZen widget:', error);
            }
        } else {
            console.error('RetentionZenWidget not available');
        }
    };

    // Inject RetentionZen script
    const injectRetentionZenScript = (propertyName: string, propertyId: string) => {
        // Don't inject if already loaded for this property
        if (scriptLoadedRef.current === propertyId) {
            return;
        }

        const userEmail = getUserEmail();

        // Validate required parameters
        if (!userEmail) {
            console.warn('RetentionZen: User email not available');
            return;
        }

        if (!propertyName) {
            console.warn('RetentionZen: Property name not available');
            return;
        }

        if (!propertyId) {
            console.warn('RetentionZen: Property ID not available');
            return;
        }

        // Check if script already exists in DOM
        const existingScript = document.querySelector('script[src="https://app.retentionzen.com/widget/v1/help.js"]') as HTMLScriptElement;

        if (existingScript) {
            // Script is already loaded, just reinitialize with new property
            if (window.RetentionZenWidget) {
                // Clean up previous widget if property changed
                if (scriptLoadedRef.current && scriptLoadedRef.current !== propertyId) {
                    console.log(
                        `RetentionZen: Property changed from ID: ${scriptLoadedRef.current} to ID: ${propertyId}, destroying previous widget`,
                    );
                    try {
                        if (typeof window.RetentionZenWidget.destroy === 'function') {
                            window.RetentionZenWidget.destroy();
                            console.log(`RetentionZen: Previous widget destroyed for property ID: ${scriptLoadedRef.current}`);
                        }
                    } catch (error) {
                        console.warn('Error destroying previous RetentionZen widget:', error);
                    }
                }
                initializeWidget(propertyName, propertyId, userEmail);
            } else {
                // Script exists but widget not ready, wait for it
                existingScript.onload = () => {
                    initializeWidget(propertyName, propertyId, userEmail);
                };
            }
            return;
        }

        // Clean up previous scripts if property changed
        if (scriptLoadedRef.current && scriptLoadedRef.current !== propertyId) {
            console.log(
                `RetentionZen: Property changed from ID: ${scriptLoadedRef.current} to ID: ${propertyId}, cleaning up previous script`,
            );
            cleanupScripts(getPropertyName());
        }

        // Load the widget script
        const script = document.createElement('script');
        script.src = 'https://app.retentionzen.com/widget/v1/help.js';
        script.async = true;
        script.onload = () => {
            console.log(`RetentionZen: Script loaded successfully for property: ${propertyName} with email: ${userEmail}`);
            initializeWidget(propertyName, propertyId, userEmail);
        };

        script.onerror = () => {
            console.error(`RetentionZen: Failed to load widget script for property: ${propertyName} (ID: ${propertyId}) ${userEmail}`);
        };

        console.log(`RetentionZen: Loading script for property: ${propertyName} (ID: ${propertyId}) ${userEmail}`);
        document.head.appendChild(script);
        scriptElementRef.current = script;
    };

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            return;
        }

        // Only proceed if user is logged in
        if (!isLoggedIn()) {
            // Clean up if user logs out
            if (scriptLoadedRef.current) {
                console.log(`RetentionZen: User logged out, cleaning up script for property ID: ${scriptLoadedRef.current}`);
                cleanupScripts(getPropertyName());
                scriptLoadedRef.current = null;
            }
            return;
        }

        // Get user email - required for script initialization
        const userEmail = getUserEmail();
        if (!userEmail) {
            // Don't inject if email is not available
            return;
        }

        // Check for errors - if error exists, log and cleanup
        if (locationDetailError) {
            console.error(`RetentionZen: Error loading location detail: ${locationDetailError}`);
            if (scriptLoadedRef.current) {
                console.log(`RetentionZen: Cleaning up script due to error for property ID: ${scriptLoadedRef.current}`);
                cleanupScripts(getPropertyName());
                scriptLoadedRef.current = null;
            }
            return;
        }

        // Only proceed if loading is complete (not loading)
        if (locationDetailLoading) {
            console.log('RetentionZen: Location detail is still loading, waiting...');
            return;
        }

        // Get current property name and ID
        const propertyName = getPropertyName();
        const propertyId = getPropertyId();

        // Only inject script if property is selected and loading is complete
        if (propertyName && propertyId) {
            // Small delay to ensure Redux state is fully updated
            const timeoutId = setTimeout(() => {
                injectRetentionZenScript(propertyName, propertyId);
            }, 100);

            return () => {
                clearTimeout(timeoutId);
            };
        } else {
            // Clean up if no property is selected
            if (scriptLoadedRef.current) {
                console.log(`RetentionZen: No property selected, cleaning up script for property ID: ${scriptLoadedRef.current}`);
                cleanupScripts(getPropertyName());
                scriptLoadedRef.current = null;
            }
        }
    }, [
        location.pathname,
        location.state,
        locationDetail?.id,
        locationDetail?.locationName,
        locationDetailLoading,
        locationDetailError,
        adminAccessToken,
        userInfo?.email,
    ]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (scriptLoadedRef.current) {
                console.log(`RetentionZen: Component unmounting, cleaning up script for property ID: ${scriptLoadedRef.current}`);
                cleanupScripts(getPropertyName());
            }
        };
    }, []);
};

// Extend Window interface for TypeScript
declare global {
    interface Window {
        RetentionZenWidget?: {
            init: (config: { productSlug: string; apiKey: string; accountId: string; userId: string; email: string }) => void;
            destroy?: () => void;
        };
    }
}

export default useRetentionZen;
